import { z } from 'zod'

export const PRIVACY_URL = 'https://www.evaa.chat/privacyverklaring'

export const formTheme = z.object({
    colorPrimary: z.string().regex(/^#[0-9a-f]{6}$/i),
    colorBgLayout: z.string().regex(/^#[0-9a-f]{6}$/i),
    colorBgContainer: z.string().regex(/^#[0-9a-f]{6}$/i),
    colorText: z.string().regex(/^#[0-9a-f]{6}$/i),
    fontFamily: z.enum([
        'Poppins, sans-serif',
        'Arial, sans-serif',
        'Georgia, serif',
        'system-ui, sans-serif',
        'monospace',
    ]),
    borderRadius: z.number().int().min(0).max(24),
    fontSize: z.number().int().min(16).max(20),
    controlHeight: z.number().int().min(44).max(56),
})
export const formPalette = formTheme.pick({
    colorPrimary: true,
    colorBgLayout: true,
    colorBgContainer: true,
    colorText: true,
})
export type FormPalette = z.infer<typeof formPalette>
export type PreviewAppearance = 'light' | 'dark'
export const defaultButtonLabel = (locale: string) =>
    locale === 'nl_NL' ? 'Verder via WhatsApp' : 'Continue in WhatsApp'

export const configuration = z
    .object({
        brandName: z
            .string()
            .min(1)
            .max(120)
            .refine((value) => value.trim().length > 0),
        heading: z
            .string()
            .min(1)
            .max(180)
            .refine((value) => value.trim().length > 0),
        introduction: z.string().max(2000),
        buttonLabel: z
            .string()
            .min(1)
            .max(60)
            .refine((value) => value.trim().length > 0),
        buttonTextMode: z.enum(['default', 'custom']).optional(),
        locale: z.enum(['nl_NL', 'en_US']),
        logoUrl: z.string().nullable(),
        appearance: z.enum(['system', 'light', 'dark']),
        theme: formTheme,
        darkTheme: formPalette.optional(),
    })
    .transform((value) => {
        // Older forms used this exact label to identify the translated default.
        const { buttonTextMode: storedButtonTextMode, ...rest } = value
        const buttonTextMode =
            storedButtonTextMode ??
            (['Start WhatsApp chat', 'Continue in WhatsApp', 'Verder via WhatsApp'].includes(value.buttonLabel)
                ? 'default'
                : 'custom')
        const fields = {
            ...rest,
            buttonLabel: buttonTextMode === 'default' ? defaultButtonLabel(value.locale) : value.buttonLabel,
        }
        if (value.darkTheme) return { ...fields, buttonTextMode, darkTheme: value.darkTheme }
        const preset = Object.values(presets).find((candidate) => {
            const original =
                value.appearance === 'dark' ? { ...candidate.theme, ...candidate.darkTheme } : candidate.theme
            return Object.entries(original).every(([key, token]) => value.theme[key as keyof FormTheme] === token)
        })
        if (preset) return { ...fields, buttonTextMode, ...preset }
        const { colorPrimary, colorBgLayout, colorBgContainer, colorText } = value.theme
        return {
            ...fields,
            buttonTextMode,
            theme:
                value.appearance === 'dark'
                    ? { ...value.theme, colorBgLayout: '#eef4ff', colorBgContainer: '#ffffff', colorText: '#15243b' }
                    : value.theme,
            darkTheme:
                value.appearance === 'dark'
                    ? { colorPrimary, colorBgLayout, colorBgContainer, colorText }
                    : { colorPrimary, colorBgLayout: '#14121d', colorBgContainer: '#231f30', colorText: '#f4efff' },
        }
    })
export const managedForm = z.object({
    publicId: z.string().regex(/^[abcdefghjkmnpqrstvwxyz23456789]{8}$/),
    url: z.string(),
    draft: configuration,
    published: configuration.nullable(),
    enabled: z.boolean(),
    ready: z.boolean(),
    publishingIssues: z.array(z.string()).default([]),
    version: z.number(),
})
export const publicForm = z.object({
    configuration,
    available: z.boolean(),
    version: z.number(),
    turnstileSiteKey: z.string().nullable().default(null),
})
export const receipt = z.object({
    receipt: z.string(),
    state: z.enum(['pending', 'accepted', 'attention']),
})
export type Configuration = z.infer<typeof configuration>
export type FormTheme = z.infer<typeof formTheme>
export type ManagedForm = z.infer<typeof managedForm>
export type Receipt = z.infer<typeof receipt>
export type ApplicationInput = {
    firstName: string
    lastName: string
    phone: string
    email?: string
    consent: boolean
    turnstileToken?: string
    website?: string
    formVersion: number
    idempotencyKey: string
}
export const presets: Record<string, { theme: FormTheme; darkTheme: FormPalette }> = {
    Evaa: {
        darkTheme: {
            colorPrimary: '#6996FE',
            colorBgLayout: '#141414',
            colorBgContainer: '#202020',
            colorText: '#f5f5f5',
        },
        theme: {
            colorPrimary: '#6996FE',
            colorBgLayout: '#fbfbfc',
            colorBgContainer: '#ffffff',
            colorText: '#141414',
            borderRadius: 12,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 16,
            controlHeight: 48,
        },
    },
    Ocean: {
        darkTheme: {
            colorPrimary: '#8ab4ff',
            colorBgLayout: '#101b2d',
            colorBgContainer: '#19283f',
            colorText: '#edf4ff',
        },
        theme: {
            colorPrimary: '#175cd3',
            colorBgLayout: '#eef4ff',
            colorBgContainer: '#ffffff',
            colorText: '#15243b',
            borderRadius: 12,
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            controlHeight: 48,
        },
    },
    Forest: {
        darkTheme: {
            colorPrimary: '#85d6a5',
            colorBgLayout: '#102219',
            colorBgContainer: '#1b3225',
            colorText: '#eef7ef',
        },
        theme: {
            colorPrimary: '#23734a',
            colorBgLayout: '#eef4ec',
            colorBgContainer: '#fffdf6',
            colorText: '#163d2b',
            borderRadius: 18,
            fontFamily: 'Georgia, serif',
            fontSize: 16,
            controlHeight: 48,
        },
    },
    Night: {
        darkTheme: {
            colorPrimary: '#b59aff',
            colorBgLayout: '#14121d',
            colorBgContainer: '#231f30',
            colorText: '#f4efff',
        },
        theme: {
            colorPrimary: '#6941c6',
            colorBgLayout: '#f4f0ff',
            colorBgContainer: '#ffffff',
            colorText: '#291b45',
            borderRadius: 12,
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            controlHeight: 48,
        },
    },
}
export function defaultConfiguration(heading = '', brandName = ''): Configuration {
    return {
        brandName,
        heading,
        introduction: '',
        buttonLabel: defaultButtonLabel('nl_NL'),
        buttonTextMode: 'default',
        locale: 'nl_NL',
        logoUrl: null,
        appearance: 'system',
        ...presets.Evaa,
    }
}

export function withFormLocale(value: Configuration, locale: Configuration['locale']): Configuration {
    const followsDefault = value.buttonLabel === defaultButtonLabel(value.locale)
    return {
        ...value,
        locale,
        buttonLabel: followsDefault ? defaultButtonLabel(locale) : value.buttonLabel,
        buttonTextMode: followsDefault ? 'default' : 'custom',
    }
}

// API validation and query-cache structural sharing can preserve different key orders.
export function sameConfiguration(left: Configuration | undefined, right: Configuration | null | undefined): boolean {
    const parsedLeft = configuration.safeParse(left)
    const parsedRight = configuration.safeParse(right)
    return (
        parsedLeft.success &&
        parsedRight.success &&
        JSON.stringify(parsedLeft.data) === JSON.stringify(parsedRight.data)
    )
}
